import 'antd/dist/reset.css';
import './styles/app.css';
import './styles/login.css';

import { Route, BrowserRouter as Router, Routes } from 'react-router-dom';
import { GraphQLClientProvider } from './common/GraphQLClientProvider';
import { DepartmentsProvider } from './common/useDepartments';
import { BuildVersionUpdateNotification } from './components/BuildVersionUpdateNotification';
import { SentryErrorBoundary } from './components/SentryErrorBoundary';
import { AuthFirebaseProvider, useAuth } from './core/providers/AuthFirebaseProvider';
import { FCMProvider } from './core/providers/FCMProvider';
import { getPrimaryRole } from './core/utils/get_primary_role';
import { Roles } from './core/utils/roles';
import { CenteredSpin } from './views/CenteredSpin';
import { DeliveryAdmin } from './views/DeliveryAdmin';
import { ForgotPasswordView } from './views/ForgotPasswordView';
import { LoginView } from './views/LoginView';
import { LogoutView } from './views/LogoutView';
import { PermissionDeniedView } from './views/PermissionDeniedView';

const PUBLIC_ROUTES = ['/logout', '/forgot-password'] as const;

export const App = () => {
  return (
    <Router>
      <BuildVersionUpdateNotification />
      <Routes>
        <Route path="/logout" element={<LogoutView />} />
        <Route path="/forgot-password" element={<ForgotPasswordView />} />

        {!PUBLIC_ROUTES.includes(window.location.pathname as (typeof PUBLIC_ROUTES)[number]) && (
          <Route
            path="*"
            element={
              <AuthFirebaseProvider>
                <ProtectedRoute />
              </AuthFirebaseProvider>
            }
          />
        )}
      </Routes>
    </Router>
  );
};

const ProtectedRoute = () => {
  const { status, token, userDataParams } = useAuth();

  console.log('ProtectedRoute status:', status);

  if (status === 'loading') {
    return <CenteredSpin />;
  }

  if (status === 'out') {
    console.log('Rendering LoginView');
    return <LoginView />;
  }

  if (status === 'unauthorized') {
    return <PermissionDeniedView />;
  }

  if (status === 'in') {
    if (!userDataParams) {
      console.error("ProtectedRoute: authState.status is 'in' but userDataParams is missing.");
      return <LoginView />;
    }

    const primaryRole = getPrimaryRole(userDataParams.roles);
    if (primaryRole === Roles.anonymous) {
      return <PermissionDeniedView />;
    }

    if (token) {
      return (
        <SentryErrorBoundary>
          <GraphQLClientProvider role={primaryRole} token={token} userDataParams={userDataParams}>
            <FCMProvider>
              <DepartmentsProvider>
                <DeliveryAdmin />
              </DepartmentsProvider>
            </FCMProvider>
          </GraphQLClientProvider>
        </SentryErrorBoundary>
      );
    } else {
      console.error(
        "ProtectedRoute: authState.status is 'in', user authorized, but token is missing."
      );
      return <LoginView />;
    }
  }

  console.error('ProtectedRoute: Unhandled auth status:', status);
  return <LoginView />;
};
